"use client"

import type { DefenseType } from "@/types/hero.types"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { ArmorTypeTooltip } from "@/components/ui/armor-type-tooltip"

interface ArmorBarProps {
  armor: number
  scale: number
  bonus?: number
  armorType?: DefenseType
  variant?: "game" | "shop"
}

export const ArmorBar = ({ armor, scale, bonus = 0, armorType, variant = "game" }: ArmorBarProps) => {
  const width = 48 * scale
  const height = 17 * scale
  // Shop variant has slightly different positioning and no bonus display
  const left = variant === "game" ? 191 * scale : 185 * scale
  const top = 113 * scale
  const iconSize = Math.max(12, 14 * scale)

  const barId = variant === "game" ? "armor-bar" : "shop-armor-bar"

  // Shop variant has larger font and no cursor-help
  const fontSize = variant === "game" ? Math.max(5, 6 * scale) : Math.max(7, 9 * scale)
  const cursorClass = variant === "game" ? "cursor-help" : ""

  const content = (
    <div
      id={barId}
      className={`absolute flex items-center gap-1 ${cursorClass}`}
      style={{
        left,
        top,
        width,
        height,
      }}
    >
      <img
        src="/images/armor-hero.png"
        alt="Armor"
        width={iconSize}
        height={iconSize}
        style={{ imageRendering: "pixelated" }}
      />
      <span
        className="text-white font-bold drop-shadow-lg"
        style={{
          fontSize,
          textShadow: "0 1px 2px black",
        }}
      >
        {armor}
        {variant === "game" && bonus > 0 && (
          <span className="text-green-400 text-[0.85em]"> +{bonus}</span>
        )}
      </span>
    </div>
  )

  // Shop variant never has tooltips, game variant only if armorType provided
  if (variant === "shop" || !armorType) {
    return content
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-black/95 border border-cyan-500/50 shadow-lg shadow-black/50 p-0"
        >
          <ArmorTypeTooltip armorType={armorType} armorValue={armor + bonus} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
